import { OverlayState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameRouteService } from "../services/GameRouteService";

const styles: string = css`
    :host {
        font-family: "Onesize";
        position: absolute;
        left: 0;
        top: 0;
        width: 100vw;
        min-height: 100vh;
        height: 100%;
        pointer-events: none;
    }

    .normal-overlay {
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        z-index: -1;
        opacity: 0;
        position: absolute;
        transition-duration: 300ms;
        transition-property: opacity;
        flex-direction: column;

        img {
            max-width: 400px;
            margin-top: 50px;
        }

        h2 {
            color: white;
            text-shadow:
                3px 3px 0 #000,
                -3px 3px 0 #000,
                -3px -3px 0 #000,
                3px -3px 0 #000;
        }

        .button {
            background-color:rgb(81, 55, 55);
            border: 2px solid rgb(255, 255, 255);
            border-radius: 5px; 
            padding: 5px 10px;
            margin: 0 0 10px 10px;
            text-transform: uppercase;
            cursor: pointer;
            display: inline-block;
            user-select: none;
            pointer-events: auto; /* enable pointer events */
        }

        .button.active,
        .button:hover {
            background-color: rgb(54, 42, 42);
        }
    }

    .damage-overlay {
        box-shadow: inset 0 0 100px rgba(200, 55, 55, 1);
        transition-duration: 5s;

        width: 100%;
        height: 100%;
    }
`;

export class OverlayComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    private _overlayState: OverlayState | undefined;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        void this.refreshOverlayState();
    }

    /**
     * @desc Fetches the overlay state from the server and updates the overlay state
     */
    private async refreshOverlayState(): Promise<void> {
        const overlayState: OverlayState = await this._gameRouteService.getOverlayState();
        this._overlayState = overlayState;

        // Render the overlay
        this.render();
    }

    /**
     * @desc Renders the overlay based on the current overlay state
     */
    private render(): void {
        // Check if the shadow root is available and if the overlay state is set
        if (!this.shadowRoot || !this._overlayState) {
            return;
        };

        // Default background color (for ESlint)
        let backgroundColor: string = "rgba(0, 0, 0, 0.8)";

        // Delay before fading out the damage overlay
        const fadeDelay: number = 500;

        // Load styles and create the element
        const element: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>
        `;

        // Check what type of overlay to render
        if (this._overlayState.status === "death" || this._overlayState.status === "victory") {
            // Render the normal overlay with image, text and actions
            element.push(html`
                <div class="normal-overlay">
                    ${this.renderImage()}
                    <h2>${this._overlayState.text}</h2>
                    ${this.renderActions()}
                </div>
            `);

            // Set the background color based on the overlay state
            switch (this._overlayState.status) {
                case "death":
                    backgroundColor = "rgba(255, 0, 0, 0.8)";
                    break;
                case "victory":
                    backgroundColor = "rgba(0, 255, 0, 0.8)";
                    break;
            }
        }
        // Check if the overlay state is "damage"
        else if (this._overlayState.status === "damage") {
            // Render the damage overlay
            element.push(html`
                <div class="damage-overlay"></div>
            `);
        }

        // Append the element to the shadow root
        this.shadowRoot.append(...element);

        // Differentiate between the overlays
        if (this._overlayState.status === "damage") {
            // Show the damage overlay
            this.shadowRoot.querySelector(".damage-overlay")?.setAttribute("style", css`z-index: 5; opacity: 1;`);

            // Wait 5 seconds before hiding the overlay
            setTimeout(() => {
                this.shadowRoot?.querySelector(".damage-overlay")?.setAttribute("style", css`opacity: 0; z-index: -1;`);
            }, fadeDelay);
        }
        // Check if the overlay state is "death" or "victory"
        else if (this._overlayState.status === "death" || this._overlayState.status === "victory") {
            // Show the normal overlay and load background color
            this.shadowRoot.querySelector(".normal-overlay")?.setAttribute("style", css`z-index: 5; background-color: ${backgroundColor}; opacity: 1;`);

            // Add event listener to the start-over button
            // This will remove the player session ID from local storage and reload the page
            this.shadowRoot.querySelector("#start-over")?.addEventListener("click", () => {
                localStorage.removeItem("playerSessionId");
                window.location.reload();
            });

            if (this._overlayState.status === "victory") {
                // Play victory music
                const audio: HTMLAudioElement = new Audio("/assets/audio/StayingAlive.mp3");
                void audio.play();
            }
        }

        // Check if the overlay is a one-time overlay
        // If so, execute the reset overlay action
        if (this._overlayState.oneTimeOverlay) {
            void this._gameRouteService.executeAction("reset-overlay");
        }
    }

    /**
     * @returns {HTMLElement} - The actions to show in the overlay
     * @desc Renders the actions based on the overlay state
     */
    private renderActions(): HTMLElement {
        if (!this._overlayState) {
            return html``;
        }

        let actions: HTMLElement = html``;

        // Different text on the buttons per overlay state
        switch (this._overlayState.status) {
            case "death":
                actions = html`
                    <a class="button" id="start-over">TRY AGAIN</a>
                `;
                break;
            case "victory":
                actions = html`
                    <a class="button" id="start-over">PLAY AGAIN?</a>
                `;
                break;
        }

        return actions;
    }

    // Render the image based on the overlay state
    private renderImage(): string {
        if (!this._overlayState || this._overlayState.status === "") {
            return "";
        }

        const image: string = `<img src="/assets/img/overlay/${this._overlayState.status}.gif" />`;

        return image;
    }
}
